"use client";

import { ArrowUpRight, TrendingUp } from "lucide-react";
import Link from "next/link";
import { PopText } from "@/components/motion/reveal";
import { AssetIcon } from "@/components/ui/asset-icon";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/ui/glass-card";
import { GradientText } from "@/components/ui/gradient-text";
import { Spotlight } from "@/components/ui/spotlight";
import { useAnalytics } from "@/hooks/use-analytics";
import { BASKETS, type Basket } from "@/lib/landing-data";
import { APP_URL } from "@/lib/links";
import { cn } from "@/lib/utils";

/** Deterministic sparkline path string for an SVG polyline (viewBox 0 0 120 36). */
function sparkline(seed: number, up: boolean): string {
  const n = 20;
  const w = 120;
  const h = 36;
  const vals: number[] = [];
  let v = up ? 0.35 : 0.7;
  for (let i = 0; i < n; i++) {
    const drift = (up ? 1 : -1) * 0.022;
    const wave = Math.sin((i + seed) * 0.9) * 0.06;
    v = Math.max(0.1, Math.min(0.9, v + drift + wave));
    vals.push(v);
  }
  return vals.map((val, i) => `${((i / (n - 1)) * w).toFixed(1)},${(h - val * h).toFixed(1)}`).join(" ");
}

function BasketCard({ basket, index }: { basket: Basket; index: number }) {
  const analytics = useAnalytics();
  const up = basket.change7d >= 0;
  const visible = basket.constituents.slice(0, 5);
  const extra = basket.constituents.length - visible.length;
  const points = sparkline(index * 3 + 1, up);

  return (
    <GlassCard
      className="flex h-full w-[18rem] shrink-0 flex-col gap-3 p-4 sm:w-[20rem]"
      style={{ boxShadow: `0 8px 40px -16px ${basket.accent}66` }}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">{basket.name}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{basket.tagline}</p>
        </div>
        <span
          className={cn(
            "flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold",
            up ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400",
          )}
        >
          <TrendingUp className={cn("size-3", !up && "rotate-180")} />
          {up ? "+" : ""}
          {basket.change7d}%
        </span>
      </div>

      <svg viewBox="0 0 120 36" className="h-10 w-full" preserveAspectRatio="none" aria-hidden>
        <polyline
          points={points}
          fill="none"
          stroke={basket.accent}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          style={{ filter: `drop-shadow(0 0 4px ${basket.accent}88)` }}
        />
      </svg>

      <div className="mt-auto flex items-center justify-between">
        <div className="flex -space-x-2">
          {visible.map((a) => (
            <AssetIcon key={a.symbol} asset={a} size={28} className="ring-2 ring-card" />
          ))}
          {extra > 0 && (
            <span className="grid size-7 place-items-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
              +{extra}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{basket.constituents.length} assets</span>
      </div>

      <Button asChild variant="outline" size="sm" className="w-full">
        <Link
          href={APP_URL}
          onClick={() =>
            analytics.trackButton("Buy basket", "Baskets", {
              basket: basket.name,
              button_url: APP_URL,
            })
          }
        >
          Buy basket
          <ArrowUpRight className="size-4" />
        </Link>
      </Button>
    </GlassCard>
  );
}

const Heading = () => (
  <div className="mx-auto mb-10 max-w-2xl px-4 text-center">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Baskets</p>
    <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
      <PopText>Trade the </PopText>
      <PopText delay={0.18}>
        <GradientText>thesis,</GradientText> not the ticker.
      </PopText>
    </h2>
    <p className="mx-auto mt-4 hidden max-w-xl text-lg text-muted-foreground md:block">
      Take a whole narrative in one position — curated baskets of tokenized stocks and crypto.
      One tap in, one tap out.
    </p>
  </div>
);

export function Baskets() {
  // Plain section (no full-page pin): a horizontal snap-scroll row of cards.
  // The previous pinned/sticky scrub was the source of the dead space below this
  // section, so it's gone — the cards just scroll horizontally in place.
  return (
    <section id="baskets" className="relative overflow-hidden py-16 sm:py-20">
      <Spotlight fill="rgba(168,85,247,0.16)" className="left-1/2 top-0 -translate-x-1/2" />
      <Heading />
      <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4 [scrollbar-width:none] sm:px-6 lg:px-8">
        {BASKETS.map((b, i) => (
          <div key={b.id} className="snap-start">
            <BasketCard basket={b} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}
